<template>
	<div id="mf-login" :data-track="isLogin ? 'LoginLayer' : 'RegistrationLayer'">
		<CloseButton class="login-close" @click.native="closeLayer" />
		<!--TUB-14229 Headline wird auf iPhone13 / iOS15 nicht angezeigt wenn innerhalb von login-wrapper -->
		<h2 v-if="$_isMobile" class="login-header">
			<span>{{ sectionLabels.headline }}</span>
		</h2>
		<div ref="login-wrapper" class="login-wrapper">
			<div class="login-content-wrapper">
				<h2 v-if="!$_isMobile" class="login-header">
					{{ sectionLabels.headline }}
				</h2>
				<div class="login-content-box">
					<!-- Introduction -->
					<p class="mb-5">
						<b class="u-font-weight-semibold">{{ labels.formHeadline }}</b>
						<br />
						{{ sectionLabels.formDescription }}
						<button
							type="button"
							class="text-link"
							:data-track-element="isLogin ? 'TopRegisterClick' : 'TopLoginClick'"
							@click="
								sendTracking($event);
								isLogin = !isLogin;
							"
						>
							{{ sectionLabels.switch_link }}
						</button>
					</p>
					<!-- Login-Form -->
					<LoginForm
						ref="loginForm"
						:is-login="isLogin"
						:class="$_isMobile ? 'mb-8' : 'mb-10'"
						@sendTracking="sendTracking"
					/>
					<!-- Social Login Buttons-->
					<SocialLogin ref="socialLoginComponent" :is-login="isLogin" @OnOAuthError="_onSocialOAuthError" />
				</div>
				<!-- Additional Inforamtion -->
				<aside class="login-info-box">
					<div class="login-info-box__item u-text-center" :class="{ 'has-bg-shade': $_isMobile }">
						<h3 class="login-subheader mb-8" :class="{ 'mt-4': $_isMobile }">
							{{ sectionLabels.switch_headline }}
						</h3>
						<button
							type="button"
							class="o-btn c-btn--transparent btn--transparent"
							:class="$_isMobile ? 'mb-3' : 'o-btn--full mb-12'"
							:data-track-element="isLogin ? 'BottomRegisterClick' : 'BottomLoginClick'"
							@click="
								sendTracking($event);
								isLogin = !isLogin;
							"
						>
							{{ sectionLabels.switch_action }}
						</button>
					</div>

					<div class="login-info-box__item has-bg-shade">
						<!--Mobile Benefits Area -->
						<Benefits v-if="$_isMobile && labels.benefitItems.length" :labels="labels" />
						<a
							:href="`${contextPath}/ueber-uns/`"
							class="more-link"
							data-track-element="AboutUs"
							@click="sendTracking($event)"
							>{{ labels.more_about_us }}</a
						>

						<Trust :is-login="isLogin" />
					</div>

					<!-- Footnotes -->
					<div class="login-info-box__item" :class="{ 'has-bg-shade': !$_isMobile }">
						<CaptchaText v-if="hasCaptcha" class="u-text-center" :class="{ 'u-mv-small': $_isMobile }" />
						<Footnote v-if="!isLogin" class="u-text-center u-mt-small" :class="{ 'u-mb-small': $_isMobile }" />
					</div>
				</aside>
			</div>

			<!--Desktop Benefits Area with background-image -->
			<div v-if="!$_isMobile" class="u-relative">
				<ResponsiveImage v-if="labels.benefitsBackgroundImages.length" :images="labels.benefitsBackgroundImages" />
				<Benefits v-if="labels.benefitItems.length" :labels="labels" is-desktop />
			</div>
		</div>
	</div>
</template>

<script>
import CloseButton from "@SharedVueComponents/CloseButton/CloseButton";
import SocialLogin from "@/components/SocialLogin";
import Benefits from "./components/Benefits";
import Trust from "./components/Trust";
import Footnote from "./components/Footnote";
import CaptchaText from "./components/CaptchaText";
import ResponsiveImage from "./components/ResponsiveImage";
import { detectingMobileMixin } from "./mixins/detectingMobileMixin";
import labels from "./labels.json";
import { fetchLabels } from "./services/rest.js";
import { contextPath } from "@ocm/services/ocm-object";
import OcmMessenger from "ocm_mercurius_messenger";
import { messengerPublicationTypes } from "@ocm/services/services.constants.js";
import { pushEvent } from "./services/dataLayerLogin";
import LoginForm from "@/components/LoginForm";

let loginAreaSubscriberId;

export default {
	components: {
		CloseButton,
		LoginForm,
		SocialLogin,
		Benefits,
		Trust,
		Footnote,
		CaptchaText,
		ResponsiveImage,
	},
	mixins: [detectingMobileMixin],
	data: () => ({
		contextPath,
		labels,
		isLogin: true,
		hasCaptcha: !!ocm?.captchaOptions?.publickey,
	}),
	computed: {
		sectionLabels() {
			return this.isLogin ? this.labels.login : this.labels.registration;
		},
	},
	watch: {
		isLogin() {
			this.scrollContentToTop();
		},
	},
	created() {
		this.showLoginOrRegister();
		fetchLabels().then(({ data }) => {
			this.labels = { ...this.labels, ...data };
		});
		loginAreaSubscriberId = OcmMessenger.subscribe(messengerPublicationTypes.REQUEST_LOGIN, this.resetLogin);
	},
	beforeDestroy() {
		OcmMessenger.unsubscribe(loginAreaSubscriberId);
	},
	methods: {
		_onSocialOAuthError() {
			this.$refs.loginForm.clearFormData();
		},
		closeLayer() {
			OcmMessenger.publish(messengerPublicationTypes.ABORTED_LOGIN);
			// send Tracking for Closing Layer
			if (this.isLogin) {
				pushEvent("LoginLayer", "LoginLayerClosed");
			} else {
				pushEvent("RegistrationLayer", "RegistrationLayerClosed");
			}
		},
		scrollContentToTop() {
			this.$refs["login-wrapper"].scrollTop = 0;
		},
		showLoginOrRegister() {
			const isLogin = this.getParam("login") !== null;
			const isRegistration = this.getParam("registration") !== null;

			if (isRegistration) {
				this.isLogin = false;
			} else if (isLogin || localStorage.getItem("loginTabActive")) {
				this.isLogin = true;
			}
		},
		getParam(paramName) {
			return new URLSearchParams(window.location.search).get(paramName);
		},
		sendTracking(event) {
			const trackedElement = event?.currentTarget?.dataset?.trackElement;
			pushEvent(this.isLogin ? "LoginLayer" : "RegistrationLayer", trackedElement);
		},
		resetLogin() {
			this.$refs.loginForm.clearFormData();
			this.scrollContentToTop();
			this.$refs.socialLoginComponent.resetOAuthErrors();
		},
	},
};
</script>

<style lang="scss" scoped>
$mobile-max-width: 720px;

#mf-login {
	position: relative;
	background-color: var(--color-white);
	width: 100%;
	max-width: 1440px;
	height: 100vh;
	padding: 64px 0 0;
	overflow: hidden;

	@include desktop {
		padding: 0;
	}
}

.login-wrapper {
	height: 100%;
	width: 100%;
	overflow: auto;

	@include desktop {
		display: grid;
		grid-template-columns: repeat(2, 1fr);
		overflow: auto;
	}
}

.login-content-wrapper {
	width: 100vw;
	height: 100vh;
	display: flex;
	flex-direction: column;

	@include desktop {
		width: auto;
		min-width: 432px;
		height: auto;
	}
}

.login-content-box {
	width: 400px;
	max-width: 100%;
	padding: 0 $space-m;
	margin: $space-m auto 64px;

	@include mobile {
		width: 100%;
		max-width: $mobile-max-width;
		margin-bottom: 32px;
	}
}

.login-info-box {
	@include mobile {
		width: 100%;
		max-width: $mobile-max-width;
		padding: 0 $space-m;
		margin: 0 auto;
	}

	@include desktop {
		display: flex;
		flex: 1;
		flex-direction: column;

		& .login-info-box__item {
			&:first-child {
				flex: 1; // to expand content to 100% height
			}

			& > ::v-deep(*) {
				max-width: 400px - 2 * $space-m; // max-width: 400 - 2x padding 16px
				margin-left: auto;
				margin-right: auto;
			}
		}
	}
}

.has-bg-shade {
	background-color: $color-white-shaded;
	padding-top: $spacing-base-unit * 6;
	padding-bottom: $spacing-base-unit * 6;

	& + & {
		padding-top: unset;
	}

	@include mobile {
		padding-left: $space-m;
		padding-right: $space-m;
	}
}

.login-header {
	background: var(--color-white);
	position: absolute;
	width: 100%;
	left: 0;
	top: 0;
	padding: $space-m $space-m;
	margin: 0;
	border-bottom: 2px solid $color-gray-light;
	z-index: 11;

	font-size: 26px;
	font-weight: 500;
	line-height: 1.1538461;

	@include mobile {
		span {
			display: block;
			width: 100%;
			max-width: ($mobile-max-width - (2 * $space-m));
			margin: 0 auto;
		}
	}

	@include desktop {
		position: static;
		padding-top: $space-l;
		border-bottom: unset;
		text-align: center;
	}
}

.login-subheader {
	font-size: 18px;
	font-weight: 500;
	line-height: 1.1666667;
}

.login-close {
	right: $space-m;
	top: $space-m;

	@include desktop {
		right: $space-m + 10px;
		top: $space-m + 10px;
	}
}

.more-link {
	display: block;
	text-align: center;
	padding: $space-l 0;
	text-decoration: underline;

	@include desktop {
		padding-top: $space-m;
	}
}

.btn--transparent {
	background-color: transparent !important;

	.no-touchevents &:hover,
	.no-touchevents &:focus {
		background-color: transparent !important;
	}
}

::v-deep(.text-link) {
	appearance: none;
	background: none;
	border: none;
	color: $color-black-light;
	cursor: pointer;
	display: inline;
	font-weight: 500;
	outline: none;
	padding: 0;
	text-decoration: underline;

	@media (hover: hover) {
		&:hover {
			color: $color-black;
		}
	}
}
</style>

<style>
html.popup_visible body {
	/* avoid scroller for jquery popup, which is still in use for login.vue */
	overflow: hidden;
}
</style>
